// Smart product description formatter.
// Auto-converts a run-on paragraph (or already-bulleted text) into
// structured sections with bullet lists for clean PDP rendering.
//
// Strategy:
// 1. If the author used newlines/bullet chars (•, -, *), respect them.
// 2. Otherwise detect known section headers (Material:, Sizes, N PCS … Set,
//    Product Details, Features, Care, Includes, Specifications, Highlights)
//    and split into { title, items[] } sections.
// 3. Within each section, split items by:
//      - number-prefix patterns  ("1 Flat King Size Sheet", "4 Pillow Cases")
//      - "Label: value" patterns ("King Size Sheet: 98\" x 100\"")
//      - sentence boundaries for prose
// Returns: Array<{ title: string|null, items: string[] }>

// Header matcher: returns each header occurrence with its index + label.
const HEADER_RE =
  /(?:^|\s)(Material\s*:|Sizes(?=\s+[A-Z])|\d+\s*PCS\s+[A-Z][A-Za-z\s]*?Set|Product\s+Details|Features|Care\s+Instructions?|Includes|Specifications|Highlights)(?=\s|:|$)/gi;

const clean = (s) => (s || "").replace(/\s+/g, " ").trim();

function splitNumericItems(text) {
  const items = text
    .split(/\s+(?=\d+\s+[A-Z])/)
    .map(clean)
    .filter(Boolean);
  return items.length > 1 ? items : null;
}

function splitLabelValueItems(text) {
  // Split only at boundaries where the previous char is a digit/quote/period —
  // i.e. after a measurement value, never in the middle of a label phrase.
  const items = text
    .split(/(?<=["'’.\d])\s+(?=[A-Z][A-Za-z][A-Za-z\s]*?:)/)
    .map(clean)
    .filter(Boolean);
  return items.length > 1 ? items : null;
}

function splitSentences(text) {
  return text
    .split(/(?<=[.!?])\s+(?=[A-Z])/)
    .map(clean)
    .filter(Boolean);
}

function itemsFor(title, body) {
  body = clean(body);
  if (!body) return [];
  const t = (title || "").toLowerCase();

  if (t.startsWith("material")) return [body];
  if (t.startsWith("sizes") || t.startsWith("specifications")) {
    return splitLabelValueItems(body) || [body];
  }
  if (/\d+\s*pcs/.test(t) || t.startsWith("includes")) {
    return splitNumericItems(body) || [body];
  }
  if (
    t.startsWith("product details") ||
    t.startsWith("features") ||
    t.startsWith("care") ||
    t.startsWith("highlights")
  ) {
    return splitSentences(body);
  }
  return (
    splitNumericItems(body) ||
    splitLabelValueItems(body) ||
    splitSentences(body)
  );
}

export function parseDescription(rawText) {
  if (!rawText || typeof rawText !== "string") return [];
  const text = rawText.trim();
  if (!text) return [];

  // 1. Newline-separated input → one bullet per non-empty line
  if (/\n/.test(text)) {
    const lines = text
      .split(/\r?\n+/)
      .map((l) => l.replace(/^\s*[•\-*]\s*/, "").trim())
      .filter(Boolean);
    return [{ title: null, items: lines }];
  }

  // 2. Inline bullet/pipe separators
  if (/[•|]/.test(text) && text.split(/[•|]/).length > 2) {
    const items = text.split(/[•|]+/).map(clean).filter(Boolean);
    return [{ title: null, items }];
  }

  // 3. Smart section parse via match positions
  const matches = [];
  let m;
  HEADER_RE.lastIndex = 0;
  while ((m = HEADER_RE.exec(text)) !== null) {
    // m.index points to the leading whitespace/start; recompute the actual title start
    const titleRaw = m[1];
    const titleStart = m.index + m[0].length - titleRaw.length;
    matches.push({ titleStart, title: clean(titleRaw).replace(/\s*:$/, "") });
  }

  if (matches.length === 0) {
    const items = splitSentences(text);
    return [{ title: null, items: items.length ? items : [text] }];
  }

  const sections = [];

  // Capture any prelude text before the first header
  if (matches[0].titleStart > 0) {
    const prelude = clean(text.slice(0, matches[0].titleStart));
    if (prelude) {
      sections.push({ title: null, items: splitSentences(prelude) });
    }
  }

  for (let i = 0; i < matches.length; i++) {
    const { title, titleStart } = matches[i];
    const bodyStart = titleStart + title.length;
    const bodyEnd =
      i + 1 < matches.length ? matches[i + 1].titleStart : text.length;
    const body = text.slice(bodyStart, bodyEnd).replace(/^\s*:\s*/, "");
    sections.push({ title, items: itemsFor(title, body) });
  }

  return sections.filter((s) => s.items && s.items.length);
}
